/**
 * Access tokens live in memory only — never localStorage — to limit what an
 * XSS payload could exfiltrate to something short-lived (JWT_ACCESS_TTL,
 * default 15m). The refresh token is the only thing persisted, so a page
 * reload has to re-establish the access token via /api/auth/me on boot
 * (see AuthContext) rather than reading it back directly.
 */
const REFRESH_STORAGE_KEY = 'tfleet.refreshToken'

// Stage: Railway deploys web and API as two separate services with two
// different domains, so a bare relative '/api/...' path (which only works
// when both are served from the same origin) doesn't reach the API at all.
// When VITE_API_BASE_URL is set (baked in at build time), requests go to
// that absolute origin instead; unset, this stays '' and behaves exactly as
// before — same-origin relative requests, e.g. for local dev via Vite's proxy.
export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '')

let accessToken: string | null = null
let refreshToken: string | null = localStorage.getItem(REFRESH_STORAGE_KEY)

export function setTokens(tokens: { access_token: string; refresh_token?: string }): void {
  accessToken = tokens.access_token
  if (tokens.refresh_token) {
    refreshToken = tokens.refresh_token
    localStorage.setItem(REFRESH_STORAGE_KEY, tokens.refresh_token)
  }
}

export function clearTokens(): void {
  accessToken = null
  refreshToken = null
  localStorage.removeItem(REFRESH_STORAGE_KEY)
}

export function hasRefreshToken(): boolean {
  return refreshToken !== null
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
    readonly requiresOverride?: boolean,
    readonly overrideCode?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Concurrent 401s during the same expiry must not each fire their own refresh
// call — that would race two token rotations against the single-use refresh
// token and fail all but one. Every caller awaits the same in-flight promise.
let refreshPromise: Promise<boolean> | null = null

async function refreshAccessToken(): Promise<boolean> {
  if (!refreshToken) return false
  refreshPromise ??= fetch(`${API_BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })
    .then(async (res) => {
      if (!res.ok) {
        clearTokens()
        return false
      }
      setTokens(await res.json())
      return true
    })
    .catch(() => {
      clearTokens()
      return false
    })
    .finally(() => {
      refreshPromise = null
    })
  return refreshPromise
}

interface FetchOptions extends RequestInit {
  /** Set false for routes that are inherently unauthenticated (login, forgot-password). */
  auth?: boolean
}

async function request<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { auth = true, ...init } = opts
  const headers = new Headers(init.headers)
  // A FormData body must NOT get an explicit content-type — the browser sets
  // its own multipart boundary. Only JSON bodies get this header.
  if (!(init.body instanceof FormData)) headers.set('content-type', 'application/json')
  if (auth && accessToken) headers.set('authorization', `Bearer ${accessToken}`)

  let res = await fetch(`${API_BASE}/api${path}`, { ...init, headers })

  if (res.status === 401 && auth && refreshToken) {
    if (await refreshAccessToken()) {
      headers.set('authorization', `Bearer ${accessToken}`)
      res = await fetch(`${API_BASE}/api${path}`, { ...init, headers })
    }
  }

  if (res.status === 204) return undefined as T

  const body = await res.json().catch(() => null)
  if (!res.ok) {
    const err = body?.error
    throw new ApiError(
      res.status,
      err?.code ?? 'unknown_error',
      err?.message ?? `Request failed with status ${res.status}`,
      err?.details,
      err?.requires_override,
      err?.override_code,
    )
  }
  return body as T
}

/**
 * Authenticated file download (report exports). The access token lives only in
 * memory, so a plain <a href> to the export URL could not carry it — fetch the
 * bytes with the normal auth/refresh handling and hand the browser a Blob.
 */
async function download(path: string): Promise<{ blob: Blob; filename: string }> {
  const attempt = () => {
    const headers = new Headers()
    if (accessToken) headers.set('authorization', `Bearer ${accessToken}`)
    return fetch(`${API_BASE}/api${path}`, { headers })
  }
  let res = await attempt()
  if (res.status === 401 && refreshToken && (await refreshAccessToken())) res = await attempt()
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body?.error?.code ?? 'unknown_error', body?.error?.message ?? `Download failed (${res.status})`)
  }
  const match = /filename="?([^";]+)"?/i.exec(res.headers.get('content-disposition') ?? '')
  return { blob: await res.blob(), filename: match?.[1] ?? 'report' }
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown, opts: FetchOptions = {}) =>
    request<T>(path, {
      ...opts,
      method: 'POST',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
  // Stage 7 §3: POD document upload — FormData body, see the content-type
  // handling above.
  upload: <T,>(path: string, formData: FormData) => request<T>(path, { method: 'POST', body: formData }),
  put: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) }),
  del: <T,>(path: string) => request<T>(path, { method: 'DELETE' }),
  download,
}
